'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Confetti, ConfettiRefProps } from '../confetti/confetti';
import { RewardModal } from '../reward-modal/reward-modal';
import tinycolor from 'tinycolor2';
import { Card } from '@instamenu/ui-system-next';
import { Star, Gift, Loader2, ChevronRight } from 'lucide-react';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { clsx } from 'clsx';
import {
  loyaltyCardWrapperAnimated,
  loyaltyCardStyle,
  cardHeader,
  cardHeaderTitle,
  cardHeaderSubtitle,
  cardBody,
  rewardBannerStyle,
  rewardBannerText,
  rewardBannerCtaArea,
  rewardBannerCtaTitle,
  rewardBannerCtaSub,
  punchCardContainer,
  punchCircle,
  punchCircleFilled,
  punchCircleEmpty,
  punchCircleProjected,
  punchGiftSlot,
  punchCompactContainer,
  punchCompactTrack,
  punchCompactFill,
  punchCompactCounter,
  pointsBody,
  pointsDisplay,
  cartProjection,
} from './loyalty-status.css';

type LoyaltyStatusProps = {
  programName: string;
  rewardsAvailable: number;
  pointsBalance?: number;
  accentColor?: string;
  textColor?: string;
  rewardItemGroupIds: string[];
  hideProgress?: boolean;
  cartPoints?: number;
  updating?: boolean;
  isItemBased?: boolean;
  earningThreshold?: number;
  progress?: number;
  rewardIncludesModifiers?: boolean;
  onRewardApplied?: () => void;
  onRewardClick?: () => void;
};

export const LoyaltyStatus = observer(
  ({
    programName,
    rewardsAvailable,
    pointsBalance = 0,
    accentColor,
    textColor: _textColor,
    rewardItemGroupIds,
    hideProgress,
    cartPoints = 0,
    updating = false,
    isItemBased = false,
    earningThreshold = 10,
    progress = 0,
    rewardIncludesModifiers = true,
    onRewardApplied: onRewardAppliedProp,
    onRewardClick: onRewardClickProp,
  }: LoyaltyStatusProps) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const confettiRef = useRef<ConfettiRefProps>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const isDesktop = useMediaQuery({ query: breakpoints.tablet });
    const action = isDesktop ? 'Click' : 'Tap';

    const brandColor = accentColor ?? (isItemBased ? '#E85D04' : '#34AC68');
    const brandDark = useMemo(() => tinycolor(brandColor).darken(20).toString(), [brandColor]);
    const brandTint = useMemo(
      () => tinycolor(brandColor).setAlpha(0.04).toRgbString(),
      [brandColor],
    );

    const handleRewardClick = useCallback(() => {
      if (onRewardClickProp) {
        onRewardClickProp();
      } else {
        setSidebarOpen(true);
      }
    }, [onRewardClickProp]);

    const handleRewardApplied = useCallback(() => {
      setSidebarOpen(false);
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const originX = (rect.left + rect.width / 2) / window.innerWidth;
        const originY = rect.top / window.innerHeight;
        confettiRef.current?.fire(originX, originY);
      }
      onRewardAppliedProp?.();
    }, [onRewardAppliedProp]);

    const hasReward = rewardsAvailable > 0;

    /* ── Header title ── */
    const renderHeaderTitle = () => programName;

    /* ── Punch subtitle ── */
    const renderPunchSubtitle = () => {
      const total = progress + cartPoints;
      const projectedRewards = Math.floor(total / earningThreshold);

      if (hasReward) {
        if (cartPoints > 0) {
          return `${rewardsAvailable} free ${rewardsAvailable !== 1 ? 'items' : 'item'} ready · +${cartPoints} punches this order`;
        }
        if (progress > 0) {
          return `${rewardsAvailable} free ${rewardsAvailable !== 1 ? 'items' : 'item'} ready · ${progress}/${earningThreshold} toward next`;
        }
        return `${rewardsAvailable} free ${rewardsAvailable !== 1 ? 'items' : 'item'} ready to redeem!`;
      }

      if (projectedRewards > 0) {
        return `${projectedRewards} free ${projectedRewards !== 1 ? 'items' : 'item'} on next order`;
      }
      const remaining = earningThreshold - progress;
      if (cartPoints > 0) {
        const projectedRemaining = remaining - cartPoints;
        return `${projectedRemaining} away from a free item after this order`;
      }
      return `${remaining} more ${remaining !== 1 ? 'items' : 'item'} to a free item`;
    };

    /* ── Header ── */
    const renderHeader = () => (
      <div className={cardHeader}>
        <span className={cardHeaderTitle} style={{ color: brandColor }}>
          {renderHeaderTitle()}
        </span>
        {isItemBased && (
          <span
            className={cardHeaderSubtitle}
            style={hasReward ? { color: '#374151', fontWeight: 500 } : undefined}
          >
            {renderPunchSubtitle()}
          </span>
        )}
      </div>
    );

    /* ── Punch reward banner ── */
    const renderPunchRewardBanner = () => (
      <div
        className={rewardBannerStyle}
        style={{ backgroundColor: brandTint }}
        onClick={handleRewardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRewardClick();
          }
        }}
        aria-label={`${action} to pick a free item`}
      >
        <Gift size={15} color={brandColor} style={{ flexShrink: 0 }} />
        <span className={rewardBannerText} style={{ color: brandDark }}>
          {action} to pick a free item
        </span>
        <div style={{ flex: 1 }} />
        <ChevronRight size={14} color={brandColor} style={{ flexShrink: 0 }} />
      </div>
    );

    /* ── Single row of punch dots ── */
    const renderPunchRow = (
      filled: number,
      projected: number,
      showGift: boolean,
      giftActive: boolean,
    ) => {
      const starSlots = earningThreshold - 1;
      return (
        <div className={punchCardContainer}>
          {Array.from({ length: starSlots }, (_, i) => {
            const isFilled = i < filled;
            const isProjected = !isFilled && i < filled + projected;
            return (
              <div
                key={i}
                className={clsx(
                  punchCircle,
                  isFilled
                    ? punchCircleFilled
                    : isProjected
                      ? punchCircleProjected
                      : punchCircleEmpty,
                )}
                style={
                  isFilled
                    ? { backgroundColor: brandColor }
                    : isProjected
                      ? {
                          backgroundColor: tinycolor(brandColor).setAlpha(0.15).toRgbString(),
                          border: `1.5px dashed ${brandColor}`,
                        }
                      : undefined
                }
              >
                <Star
                  size={13}
                  color={isFilled || isProjected ? '#fff' : '#D1D5DB'}
                  fill={isFilled || isProjected ? '#fff' : 'none'}
                />
              </div>
            );
          })}
          {showGift && (
            <div
              className={punchGiftSlot}
              style={
                giftActive
                  ? {
                      borderColor: brandColor,
                      borderStyle: 'dashed',
                      backgroundColor: tinycolor(brandColor).setAlpha(0.1).toRgbString(),
                    }
                  : undefined
              }
            >
              <Gift size={12} color={giftActive ? brandColor : '#9CA3AF'} />
            </div>
          )}
        </div>
      );
    };

    /* ── Punch dots ── */
    const renderPunchDots = () => {
      if (earningThreshold <= 12) {
        const total = progress + cartPoints;
        const overflows = total >= earningThreshold;

        if (overflows) {
          const rows: { filled: number; projected: number; giftActive: boolean }[] = [];
          // First row: current progress + projected to fill threshold
          rows.push({ filled: progress, projected: earningThreshold - progress, giftActive: true });
          // Remaining projected points after first threshold
          let remaining = total - earningThreshold;
          while (remaining > 0) {
            const projectedInRow = Math.min(remaining, earningThreshold);
            rows.push({
              filled: 0,
              projected: projectedInRow,
              giftActive: projectedInRow >= earningThreshold,
            });
            remaining -= earningThreshold;
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
              {rows.map((row, idx) => (
                <div key={idx}>
                  {renderPunchRow(row.filled, row.projected, true, row.giftActive)}
                </div>
              ))}
            </div>
          );
        }

        // Normal case: single row
        const giftProjected = total > earningThreshold - 1;
        return renderPunchRow(progress, cartPoints, true, giftProjected);
      }

      return (
        <div className={punchCompactContainer}>
          <div className={punchCompactTrack}>
            {cartPoints > 0 && (
              <div
                className={punchCompactFill}
                style={{
                  width: `${Math.min(((progress + cartPoints) / earningThreshold) * 100, 100)}%`,
                  backgroundColor: brandColor,
                  opacity: 0.3,
                }}
              />
            )}
            <div
              className={punchCompactFill}
              style={{
                width: `${earningThreshold > 0 ? Math.min((progress / earningThreshold) * 100, 100) : 0}%`,
                backgroundColor: brandColor,
              }}
            />
          </div>
          <span className={punchCompactCounter}>
            {progress}/{earningThreshold}
          </span>
        </div>
      );
    };

    /* ── Points reward banner ── */
    const renderPointsRewardBanner = () => (
      <div
        className={rewardBannerStyle}
        style={{ backgroundColor: brandTint }}
        onClick={handleRewardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRewardClick();
          }
        }}
        aria-label="Redeem points"
      >
        <div className={pointsDisplay}>{pointsBalance.toLocaleString()} pts</div>
        <div className={rewardBannerCtaArea}>
          <div className={rewardBannerCtaTitle} style={{ color: brandColor }}>
            Redeem your points
          </div>
          <div className={rewardBannerCtaSub}>{action} to pick a free item</div>
        </div>
        <div style={{ flex: 1 }} />
        <ChevronRight size={14} color={brandColor} style={{ flexShrink: 0 }} />
      </div>
    );

    /* ── Body ── */
    const renderBody = () => {
      if (hideProgress) return null;

      if (isItemBased) {
        if (hasReward) {
          return (
            <>
              <div className={cardBody}>{renderPunchDots()}</div>
              {renderPunchRewardBanner()}
            </>
          );
        }
        return <div className={cardBody}>{renderPunchDots()}</div>;
      }

      // Points-based: reward available
      if (hasReward) return renderPointsRewardBanner();

      // Points-based: updating
      if (updating) {
        return (
          <div className={cardBody}>
            <Loader2 size={18} color="#9CA3AF" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        );
      }

      // Points-based: balance display
      return (
        <div className={pointsBody}>
          <div className={pointsDisplay}>{pointsBalance.toLocaleString()} pts</div>
          {cartPoints > 0 && (
            <div className={cartProjection} style={{ color: brandColor }}>
              +{cartPoints} from this order
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        <div className={loyaltyCardWrapperAnimated}>
          <Card ref={cardRef} className={loyaltyCardStyle} css={{ padding: 0 }}>
            {renderHeader()}
            {renderBody()}
          </Card>
        </div>

        <RewardModal
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onRewardApplied={handleRewardApplied}
          rewardItemGroupIds={rewardItemGroupIds}
          pointsBalance={pointsBalance}
          isItemBased={isItemBased}
          earningThreshold={earningThreshold}
          rewardsAvailable={rewardsAvailable}
          progress={progress}
          rewardIncludesModifiers={rewardIncludesModifiers}
        />
        <Confetti ref={confettiRef} />
      </>
    );
  },
);
